/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RoutePath } from 'shared/enums/RoutePath';

import { Breadcrumbs, Typography } from '@material-ui/core';
import { useLinkStyles } from 'components/common/useLinkStyles';
import { useUserAccounts } from '../../redux/features/user/userAccounts/useUserAccounts';

import { SwitchAccountCard } from './components/SwitchAccountCard';
import { useSwitchAccount } from './components/useSwitchAccount';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: theme.spacing(4),
  },
  cardSection: {
    display: 'grid',
    gridGap: '1rem',
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
    [theme.breakpoints.up('xl3')]: {
      gridGap: '2.5rem',
    },
  },
  avatarPrimary: {
    color: theme.palette.avatar.primary.text,
    backgroundColor: theme.palette.avatar.primary.background,
  },
  avatarSecondary: {
    color: theme.palette.avatar.secondary.text,
    backgroundColor: theme.palette.avatar.secondary.background,
  },
}));

function SwitchAccount() {
  const { t } = useTranslation();
  document.title = t('user-management.switch-account');
  const classes = useStyles();
  const linkClass = useLinkStyles();
  const { user, handleChangeOrganizationId } = useUserAccounts();
  const { shouldRefreshToken } = useSwitchAccount();

  if (shouldRefreshToken) return <div />;

  const mainUserName = `${user?.firstName} ${user?.lastName}`;

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" className="breadcrumbs">
        <Link className={linkClass.link} to={RoutePath.DASHBOARD.path}>
          {t('front.office.dashboard')}
        </Link>
        <Typography color="textPrimary">{t('user-management.switch-account')}</Typography>
      </Breadcrumbs>

      <Typography variant="h1" component="h2" className={classes.title}>
        {t('user-management.select-account')}
      </Typography>

      <div className={classes.cardSection}>
        {user?.groups?.map((organization) => {
          const orgName = user?.id ? organization?.name : 'Org';
          const orgNameInitial = organization?.name ? organization?.name : mainUserName;

          return (
            <SwitchAccountCard
              className={classes.avatarPrimary}
              name={orgName}
              initials={orgNameInitial}
              key={organization?.id}
              color={organization?.attributes?.accentColor}
              id={organization?.id}
              aria-label={organization?.id}
              type={`${t(organization?.type)} account`}
              roles={organization?.roles}
              onClick={() => handleChangeOrganizationId(organization?.id)}
            />
          );
        })}
      </div>
    </>
  );
}

export default SwitchAccount;
